import { z } from "zod";

export const PeppolProvider = z.enum(["IONITE", "STORECOVE", "GETPEPPR", "PEPPOL_SH"]);
export type PeppolProviderType = z.infer<typeof PeppolProvider>;

export const PeppolEnvironment = z.enum(["SANDBOX", "PRODUCTION"]);
export type PeppolEnvironmentType = z.infer<typeof PeppolEnvironment>;

export const PeppolOnboardingStatus = z.enum([
  "UNASSIGNED",
  "VERIFYING",
  "AUTHORIZATION_REQUIRED",
  "PROVISIONING",
  "ACTIVE",
  "ERROR",
  "SUSPENDED",
  "ARCHIVED",
]);
export type PeppolOnboardingStatusType = z.infer<typeof PeppolOnboardingStatus>;

export const PeppolSendOutcome = z.enum(["ACCEPTED", "REJECTED", "UNKNOWN"]);
export type PeppolSendOutcomeType = z.infer<typeof PeppolSendOutcome>;

export const PeppolTransmissionStatus = z.enum([
  "PENDING",
  "SUBMITTING",
  "ACCEPTED",
  "DELIVERED",
  "REJECTED",
  "FAILED",
  "UNKNOWN",
]);
export type PeppolTransmissionStatusType = z.infer<typeof PeppolTransmissionStatus>;

export const PeppolProductionIneligibilityReason = z.enum([
  "PROVIDER_CONTRACT_REQUIRED",
  "EXACT_UBL_SEND_UNVERIFIED",
  "INBOUND_DELIVERY_UNVERIFIED",
  "WEBHOOK_REPLAY_CONTRACT_UNVERIFIED",
  "COUNTRY_NOT_QUALIFIED",
  "CREDENTIALS_NOT_CONFIGURED",
  "PRODUCTION_DISABLED",
]);
export type PeppolProductionIneligibilityReasonType = z.infer<
  typeof PeppolProductionIneligibilityReason
>;

export const PeppolParticipantIdentifierSchema = z.object({
  scheme: z.string().trim().min(2).max(16).regex(/^[0-9A-Za-z._-]+$/),
  value: z.string().trim().min(1).max(128).regex(/^[^\s:]+$/),
}).strict();
export type PeppolParticipantIdentifier = z.infer<typeof PeppolParticipantIdentifierSchema>;

export const PeppolProviderAssignmentSchema = z.object({
  tenantId: z.string().trim().min(1).max(128),
  provider: PeppolProvider,
  environment: PeppolEnvironment,
  externalLegalEntityId: z.string().trim().min(1).max(255).optional(),
  participantId: z.string().trim().min(3).max(255).optional(),
  status: PeppolOnboardingStatus,
  providerStatus: z.string().trim().max(128).optional(),
  countryCode: z.string().trim().length(2).transform((value) => value.toUpperCase()),
  version: z.number().int().positive(),
  lastSynchronizedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}).strict();
export type PeppolProviderAssignment = z.infer<typeof PeppolProviderAssignmentSchema>;

export const PeppolProviderCapabilitiesSchema = z.object({
  provider: PeppolProvider,
  environment: PeppolEnvironment,
  provisioning: z.boolean(),
  participantLookup: z.boolean(),
  exactUblSend: z.boolean(),
  outboundStatus: z.boolean(),
  inboundUbl: z.boolean(),
  productionEligible: z.boolean(),
  productionIneligibilityReasons: z.array(PeppolProductionIneligibilityReason),
  supportedCountryCodes: z.array(z.string().length(2)).default([]),
}).strict();
export type PeppolProviderCapabilities = z.infer<typeof PeppolProviderCapabilitiesSchema>;

export const PeppolTenantStatusSchema = z.object({
  assignment: PeppolProviderAssignmentSchema.nullable(),
  capabilities: PeppolProviderCapabilitiesSchema.nullable(),
  sendEligible: z.boolean(),
  receiveEligible: z.boolean(),
  countryEligible: z.boolean(),
  nextAction: z.enum([
    "CONTACT_SYSTEM_ADMIN",
    "START_ONBOARDING",
    "COMPLETE_AUTHORIZATION",
    "WAIT_FOR_VERIFICATION",
    "CONTACT_SUPPORT",
    "NONE",
  ]),
}).strict();
export type PeppolTenantStatus = z.infer<typeof PeppolTenantStatusSchema>;

// Only Store status on the tenant, not keys.
export const PeppolStatus = z.enum(["NOT_REGISTERED", "PENDING", "REGISTERED", "ERROR"]);

// Simplified configuration schema for Tenant (only what they see/control)
// They can only see their status and maybe their Peppol Participant ID.
export const PeppolConfigSchema = z.object({
  status: PeppolStatus.default("NOT_REGISTERED"),
  participantId: z.string().trim().optional(), // e.g. 9930:DE...
  provider: PeppolProvider.default("IONITE"), // Which provider is handling them (Platform default likely)
});

export type PeppolConfig = z.infer<typeof PeppolConfigSchema>;

// System Level Config (Not stored in Tenant, but used by Services)
export const SystemPeppolConfigSchema = z.object({
    activeProvider: PeppolProvider,
    ioniteApiKey: z.string().optional(),
    ioniteEnvironment: z.enum(["sandbox", "production"]).optional(),
    ioniteOrganizationId: z.string().trim().optional(),
    storecoveApiKey: z.string().optional(),
});
